import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ROLE_ORG_MEMBER, ROLE_GRC_ADMIN, ROLE_AUDIT_USER, ROLE_RISK_ANALYST, ROLE_C_SUITE, ROLE_COSTING_USER, UserRoleNumber, authService } from '../../services/userService';
import './Login.css';
import FormControl from '../common/FormControl';
import Toast, { ToastMessage } from '../common/Toast';

/**
 * Login Component with Authentication Context Integration
 * 
 * This component provides secure user authentication with:
 * - Form validation with real-time feedback
 * - Password visibility toggle functionality
 * - Remember me functionality using localStorage
 * - Professional UI with animations and accessibility
 * - Integration with AuthContext for session management
 * 
 * @author AnyGRC System
 * @created 2025-01-27
 * @updated 2025-12-12 - Added Costing Persona
 */

interface LoginFormData {
  login_id: string;
  password: string;
  role: UserRoleNumber | 0;  // Allow 0 for no selection
}

interface LoginErrors {
  login_id?: string;
  password?: string;
  role?: string;
  general?: string;
}

const Login: React.FC = () => {
  const { state, login, clearError } = useAuth();

  const [formData, setFormData] = useState<LoginFormData>({
    login_id: '',
    password: '',
    role: 0,  // Default to 0 (No selection)
  });

  const [entities, setEntities] = useState<{ id: number; name: string }[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<number | ''>('');

  const [errors, setErrors] = useState<LoginErrors>({});
  const [rememberMe, setRememberMe] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  // Fetch entities on mount
  useEffect(() => {
    const fetchEntities = async () => {
      try {
        const data = await authService.getPublicEntities();
        console.log('Entities fetched:', data);
        setEntities(data);
        if (data.length > 0) {
          const savedEntityId = localStorage.getItem('anygrc_last_entity');
          const savedEntity = savedEntityId ? data.find((e: { id: number }) => e.id === Number(savedEntityId)) : null;
          setSelectedEntity(savedEntity ? savedEntity.id : data[0].id);
        }
      } catch (error) {
        console.error('Failed to fetch entities:', error);
        setToast({
          type: 'error',
          message: 'Failed to load entities. Please check backend connection.'
        });
      }
    };
    fetchEntities();
  }, []);

  // Clear errors when auth error changes
  useEffect(() => {
    if (state.error) {
      setErrors(prev => ({ ...prev, general: state.error || undefined }));
    }
  }, [state.error]);

  // Load remembered credentials on component mount
  useEffect(() => {
    const savedLoginId = localStorage.getItem('anygrc_remembered_login_id');
    const wasRemembered = localStorage.getItem('anygrc_remember_me') === 'true';

    if (savedLoginId && wasRemembered) {
      setFormData(prev => ({ ...prev, login_id: savedLoginId }));
      setRememberMe(true);
    }
  }, []);

  /**
   * Handle input field changes with validation
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === 'entity') {
      setSelectedEntity(Number(value));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear field-specific errors on change
    if (errors[name as keyof LoginErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }

    // Clear general error and toast
    if (errors.general) {
      setErrors(prev => ({ ...prev, general: undefined }));
      clearError();
      setToast(null);
    }
  };

  /**
   * Validate form data
   */
  const validateForm = (): boolean => {
    const newErrors: LoginErrors = {};

    // Validate login_id
    if (!formData.login_id) {
      newErrors.login_id = 'Login ID is required';
    }

    // Validate password
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 3) {  // Temporarily reduced from 6 to 3 for testing
      newErrors.password = 'Password must be at least 3 characters';
    }

    if (Object.keys(newErrors).length > 0) {
      const errorMessages = Object.values(newErrors).filter(Boolean);
      setToast({
        type: 'error',
        message: errorMessages.join(' • ')
      });
      return false;
    }

    return true;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setErrors({});
    setToast(null);

    try {
      // Ensure role is valid before calling login
      const roleToSubmit = formData.role === 0 ? ROLE_ORG_MEMBER : formData.role;
      const success = await login(formData.login_id, formData.password, rememberMe, String(roleToSubmit), Number(selectedEntity));

      if (success) {
        // Save user ID and role for remember me functionality
        if (rememberMe) {
          localStorage.setItem('anygrc_remembered_login_id', formData.login_id);
          localStorage.setItem('anygrc_remember_me', 'true');
        } else {
          localStorage.removeItem('anygrc_remembered_login_id');
          localStorage.removeItem('anygrc_remember_me');
        }

        // Store the selected role as number
        localStorage.setItem('anygrc_user_role', String(roleToSubmit));
        // Remember last selected entity for next login
        if (selectedEntity !== '') {
          localStorage.setItem('anygrc_last_entity', String(selectedEntity));
        }

        // Clear form
        setFormData({ login_id: '', password: '', role: 0 });
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      setToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Login failed. Please try again.'
      });
    }
  };
  /**
   * Handle login triggered by role selection
   */
  const handleRoleLogin = async (selectedRole: UserRoleNumber) => {
    // 1. Update the role in state immediately for UI feedback
    setFormData(prev => ({ ...prev, role: selectedRole }));

    // 2. Validate Login ID and Password
    const newErrors: LoginErrors = {};
    if (!formData.login_id) {
      newErrors.login_id = 'Login ID is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 3) {
      newErrors.password = 'Password must be at least 3 characters';
    }

    if (Object.keys(newErrors).length > 0) {
      const errorMessages = Object.values(newErrors).filter(Boolean);
      setToast({
        type: 'error',
        message: errorMessages.join(' • ')
      });
      return;
    }

    // 3. Proceed with Login
    setToast(null);

    try {
      const success = await login(formData.login_id, formData.password, rememberMe, String(selectedRole), Number(selectedEntity));

      if (success) {
        // Save user ID and role for remember me functionality
        if (rememberMe) {
          localStorage.setItem('anygrc_remembered_login_id', formData.login_id);
          localStorage.setItem('anygrc_remember_me', 'true');
        } else {
          localStorage.removeItem('anygrc_remembered_login_id');
          localStorage.removeItem('anygrc_remember_me');
        }

        // Store the selected role as number
        localStorage.setItem('anygrc_user_role', String(selectedRole));
        // Remember last selected entity for next login
        if (selectedEntity !== '') {
          localStorage.setItem('anygrc_last_entity', String(selectedEntity));
        }

        // Clear form
        setFormData({ login_id: '', password: '', role: 0 });
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      setToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Login failed. Please try again.'
      });
    }
  };

  /**
   * Handle demo login (if needed for testing)
   */
  const handleDemoLogin = () => {
    setFormData({
      login_id: 'grcadmin',
      password: 'admin123',
      role: ROLE_ORG_MEMBER
    });
    setRememberMe(false);
  };

  return (
    <div className="login-split-container">
      {/* Left Side: Marketing Content (Moved from Right) */}
      <div className="login-right-section">
        <div className="login-background-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>

        <aside className="oversight-panel" aria-label="AnyGRC value proposition">
          <header className="oversight-header" style={{ textAlign: 'center' }}>
            {/* Removed top eagle icon */}
            <h2 className="oversight-heading">Oversight Elevated</h2>

            {/* Refined Transformation List - Sober Style */}
            {/* Refined Transformation List - 2 Column Grid */}
            <div className="oversight-steps" style={{ marginTop: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 20px', fontSize: '15px', fontWeight: '500', color: 'rgba(255,255,255,0.95)', textAlign: 'left' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span><strong>Governance</strong> <span style={{ opacity: 0.7 }}>→</span> Compliance</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span><strong>Risk</strong> <span style={{ opacity: 0.7 }}>→</span> Control Mitigation</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span><strong>Warning</strong> <span style={{ opacity: 0.7 }}>→</span> Assurance</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span><strong>Monitoring</strong> <span style={{ opacity: 0.7 }}>→</span> Correction</span>
              </div>
            </div>
          </header>

          <section className="oversight-pillars" aria-label="Core capability pillars">
            {/* Pillars content remains same */}
            <div className="pillar" aria-label="Governance Assurance">
              <h3>Governance Assurance</h3>
              <p>Policies aligned, ownership clear, accountability tracked.</p>
            </div>
            <div className="pillar" aria-label="Risk Anticipation">
              <h3>Risk Anticipation</h3>
              <p>Emerging trends surfaced before they escalate.</p>
            </div>
            <div className="pillar" aria-label="Control Integrity">
              <h3>Control Integrity</h3>
              <p>Control design & execution verified in real time.</p>
            </div>
            <div className="pillar" aria-label="Rapid Alerts">
              <h3>Rapid Alerts</h3>
              <p>Anomalies flagged within minutes — not weeks.</p>
            </div>
            <div className="pillar" aria-label="Unified Evidence">
              <h3>Unified Evidence</h3>
              <p>Single source for audits, regulators & leadership.</p>
            </div>
            <div className="pillar" aria-label="Adaptive Intelligence">
              <h3>Adaptive Intelligence</h3>
              <p>Signals learn and refine with every cycle.</p>
            </div>
          </section>

          <footer className="oversight-footer" aria-label="Closing value message" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: '30px', marginTop: 'auto', paddingTop: '30px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <img
              src={process.env.PUBLIC_URL + '/eagle.png'}
              alt="AnyGRC Eagle"
              style={{ height: '100px', width: 'auto', filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.4))' }}
            />
            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase', color: '#fff', whiteSpace: 'nowrap' }}>
                Altitude • Vision • Speed • Precision
              </h4>
              <p style={{ margin: 0, fontSize: '16px', fontStyle: 'italic', opacity: 0.9, fontWeight: '500' }}>
                Attack the risk before it lands.
              </p>
            </div>
          </footer>
        </aside>
      </div>

      {/* Right Side: Login Form (Moved from Left) */}
      <div className="login-left-section">
        <div className="login-wrapper">
          <div className="login-header">
            <div className="login-logo" style={{ marginBottom: '10px' }}> {/* Reduced margin */}
              <img
                src={process.env.PUBLIC_URL + '/anygrc-logo.png'}
                alt="AnyGRC Logo"
                style={{
                  height: '10vh',
                  maxHeight: '100px',
                  display: 'block',
                  maxWidth: '100%',
                  objectFit: 'contain'
                }}
              />
            </div>
            <p className="login-subtitle" style={{ fontSize: '18px', fontWeight: '600', color: '#334155', letterSpacing: '0.5px', marginTop: '0' }}> {/* Removed top margin */}
              Governance <span style={{ color: '#cbd5e1', margin: '0 8px' }}>|</span> Risk <span style={{ color: '#cbd5e1', margin: '0 8px' }}>|</span> Compliance
            </p>
          </div>

          <form className="login-form" onSubmit={handleSubmit}>
            {/* Entity Selection Field */}
            <div className="form-group">
              <FormControl.Select
                label="Entity"
                required
                id="entity"
                name="entity"
                value={selectedEntity}
                onChange={handleInputChange}
                disabled={state.isLoading || entities.length === 0}
                data-testid="SEL_login_entity"
              >
                <option value="" disabled>Select Entity</option>
                {entities.map(entity => (
                  <option key={entity.id} value={entity.id}>
                    {entity.name}
                  </option>
                ))}
              </FormControl.Select>
            </div>

            {/* Login ID Field */}
            <div className="form-group">
              <FormControl.Input
                label="Login ID"
                required
                type="text"
                id="login_id"
                name="login_id"
                value={formData.login_id}
                onChange={handleInputChange}
                placeholder="Enter your Login ID"
                disabled={state.isLoading}
                autoComplete="username"
                data-testid="INP_login_username"
              />
            </div>

            {/* Password Field */}
            <div className="form-group">
              <FormControl.Input
                label="Password"
                required
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                disabled={state.isLoading}
                autoComplete="current-password"
                data-testid="INP_login_password"
              />
            </div>

            {/* Role Selection - Triggers Login Immediately */}
            <div className="form-group role-group" style={{ textAlign: 'center' }}>
              <label className="form-label" style={{
                display: 'inline-block',
                color: '#6d28d9',
                fontSize: '14px',
                fontWeight: '800',
                letterSpacing: '1px',
                marginBottom: '1vh',
                textTransform: 'uppercase'
              }}>
                LOGIN AS
              </label>
              <div className="role-selection-container">
                {/* Row 1: GRC Admin and C-Suite (Executive) */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                  <button
                    type="button"
                    className={`role-btn role-btn-admin ${formData.role === ROLE_GRC_ADMIN ? 'active' : ''}`}
                    onClick={() => handleRoleLogin(ROLE_GRC_ADMIN)}
                    disabled={state.isLoading}
                    title="Sign in as GRC Admin"
                    style={{ flexDirection: 'row', padding: '12px', gap: '8px', justifyContent: 'center' }}
                    data-testid="BTN_login_role_grc_admin"
                  >
                    <span className="role-btn-icon" style={{ fontSize: '24px' }}>👨‍💼</span>
                    <span className="role-btn-text" style={{ fontSize: '14px' }}>GRC Team</span>
                  </button>

                  <button
                    type="button"
                    className={`role-btn role-btn-csuite ${formData.role === ROLE_C_SUITE ? 'active' : ''}`}
                    onClick={() => handleRoleLogin(ROLE_C_SUITE)}
                    disabled={state.isLoading}
                    title="Sign in as C-Suite Leadership"
                    style={{ flexDirection: 'row', padding: '12px', gap: '8px', justifyContent: 'center' }}
                    data-testid="BTN_login_role_executive"
                  >
                    <span className="role-btn-icon" style={{ fontSize: '24px' }}>👑</span>
                    <span className="role-btn-text" style={{ fontSize: '14px' }}>Executive</span>
                  </button>
                </div>

                {/* Row 2: Staff, Risk, Audit, Costing */}
                <div className="role-buttons" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                  <button
                    type="button"
                    className={`role-btn role-btn-staff ${formData.role === ROLE_ORG_MEMBER ? 'active' : ''}`}
                    onClick={() => handleRoleLogin(ROLE_ORG_MEMBER)}
                    disabled={state.isLoading}
                    title="Sign in as Organisation Member"
                    data-testid="BTN_login_role_staff"
                  >
                    <span className="role-btn-icon">🏢</span>
                    <span className="role-btn-text">Staff</span>
                  </button>

                  <button
                    type="button"
                    className={`role-btn role-btn-risk ${formData.role === ROLE_RISK_ANALYST ? 'active' : ''}`}
                    onClick={() => handleRoleLogin(ROLE_RISK_ANALYST)}
                    disabled={state.isLoading}
                    title="Sign in as Risk Analyst"
                    data-testid="BTN_login_role_risk"
                  >
                    <span className="role-btn-icon">📊</span>
                    <span className="role-btn-text">Risk</span>
                  </button>

                  <button
                    type="button"
                    className={`role-btn role-btn-audit ${formData.role === ROLE_AUDIT_USER ? 'active' : ''}`}
                    onClick={() => handleRoleLogin(ROLE_AUDIT_USER)}
                    disabled={state.isLoading}
                    title="Sign in as Audit User"
                    data-testid="BTN_login_role_audit"
                  >
                    <span className="role-btn-icon">🔍</span>
                    <span className="role-btn-text">Audit</span>
                  </button>

                  <button
                    type="button"
                    className={`role-btn role-btn-costing ${formData.role === ROLE_COSTING_USER ? 'active' : ''}`}
                    onClick={() => handleRoleLogin(ROLE_COSTING_USER)}
                    disabled={state.isLoading}
                    title="Sign in as Costing User"
                    data-testid="BTN_login_role_costing"
                  >
                    <span className="role-btn-icon">💰</span>
                    <span className="role-btn-text">Costing</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Remember Me and Fill Defaults Row */}
            <div className="form-options-row">
              <FormControl.Checkbox
                label="Remember me"
                name="rememberMe"
                checked={rememberMe}
                onChange={(checked) => {
                  setRememberMe(checked);
                  if (!checked) {
                    localStorage.removeItem('anygrc_remembered_login_id');
                    localStorage.removeItem('anygrc_remember_me');
                  }
                }}
                disabled={state.isLoading}
                data-testid="CHK_login_remember_me"
              />
              <button
                type="button"
                className="fill-defaults-btn"
                onClick={handleDemoLogin}
                disabled={state.isLoading}
                data-testid="BTN_login_fill_defaults"
              >
                Fill Defaults
              </button>
            </div>

            {/* Loading Indicator (replaces Submit Button) */}
            {state.isLoading && (
              <div className="login-loading-indicator">
                <span className="loading-spinner"></span>
                <span>Signing In...</span>
              </div>
            )}
          </form>

          <div className="login-footer">
            <p className="security-notice">
              <span className="security-icon">🔐</span>
              Your connection is secure and encrypted
            </p>
            <div className="support-links">
              <a href="#help" className="support-link" data-testid="LNK_login_need_help">Need Help?</a>
              <span className="separator">•</span>
              <a href="#contact" className="support-link" data-testid="LNK_login_contact_support">Contact Support</a>
            </div>
          </div>
        </div>
      </div>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default Login;